import { Component, OnInit, inject } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, Validators, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { JobsService } from 'nebula-apis/workflow';
import { MatFormField, MatLabel, MatSuffix, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    template: `
        <mat-form-field appearance="fill">
            <mat-label>Name</mat-label>
            <input matInput [formControl]="formControl" />
            @switch (formControl.status) {
                @case ('VALID') {
                    <mat-icon matSuffix color="primary">check_circle</mat-icon>
                }
                @case ('PENDING') {
                    <mat-spinner matSuffix [diameter]="20" [style.margin-right.px]="10"></mat-spinner>
                }
                @case ('INVALID') {
                    <mat-icon matSuffix color="error">error</mat-icon>
                }
                @default {
                    <mat-icon matSuffix>code</mat-icon>
                }
            }
            <mat-hint align="end"> {{ formControl.value?.length || 0 }}/{{ MAX_VALUE_LENGTH }} </mat-hint>
            @if (formControl.hasError('required')) {
                <mat-error>You must provide a name.</mat-error>
            }
            @if (formControl.hasError('minlength')) {
                <mat-error>Name must have at least {{ MIN_VALUE_LENGTH }} characters.</mat-error>
            }
            @if (!formControl.hasError('required') && formControl.hasError('uniqueName')) {
                <mat-error>{{ formControl.getError('uniqueName') }}</mat-error>
            }
            @if (!formControl.hasError('required') && formControl.hasError('invalidStartCharacter')) {
                <mat-error>{{ formControl.getError('invalidStartCharacter') }}</mat-error>
            }
            @if (!formControl.hasError('required') && formControl.hasError('invalidCharacter')) {
                <mat-error>
                    {{ formControl.getError('invalidCharacter') }}
                </mat-error>
            }
            <mat-hint>Unique name identifier.</mat-hint>
        </mat-form-field>
    `,
    imports: [MatFormField, MatLabel, MatInput, ReactiveFormsModule, MatIcon, MatSuffix, MatProgressSpinner, MatHint, MatError]
})
export class InputNameComponent extends FieldType implements OnInit {
    private job = inject(JobsService);

    public readonly MIN_VALUE_LENGTH: number = 3;
    public readonly MAX_VALUE_LENGTH: number = 60;

    private initialTextValue!: string;

    ngOnInit(): void {
        this.formControl.setValidators([
            this.props.required ? Validators.required : (undefined as any),
            this.isStartCharacterValidValidator(),
            this.isNameValidValidator(),
            Validators.minLength(this.MIN_VALUE_LENGTH),
            Validators.maxLength(this.MAX_VALUE_LENGTH)
        ]);
        this.formControl.setAsyncValidators(this.isNameAvailableAsyncValidator());
        this.initialTextValue = this.formControl.value.slice(); // copy string value without reference
    }

    isNameAvailableAsyncValidator =
        (): AsyncValidatorFn =>
        (control: AbstractControl): Observable<ValidationErrors | null> => {
            const name = control.value;
            return name && name !== this.initialTextValue
                ? this.job.v1JobsGet({ category: undefined, name: control.value }).pipe(
                      take(1),
                      catchError(error => of(null)),
                      map(response => (response?.results?.length === 0 || control.pristine ? null : { uniqueName: `This name is already taken.` }))
                  )
                : of(null);
        };

    isStartCharacterValidValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const invalidMatcher = /^\S/;
            const valid = invalidMatcher.test(control.value);
            return valid
                ? null
                : {
                      invalidStartCharacter: 'The first character must be a letter.'
                  };
        };
    }

    isNameValidValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const invalidMatcher = /^[a-zA-Z0-9_\-\s]*$/;
            const valid = invalidMatcher.test(control.value);
            return valid
                ? null
                : {
                      invalidCharacter: 'You may only use letters, numbers or "_ -" symbols.'
                  };
        };
    }
}
